<script lang="ts" setup>
import { storeToRefs } from "pinia";
import IndexGrid from "~/components/Indexs/IndexGrid.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import type { IPaginationApi } from "~/utils/interfaces/table-interfaces";
import Tag from "~/components/Common/Tag.vue";
import { ScreenEnumType } from "~/utils/enums/responsive.enums";
import { useMedia } from "~/composables/utils/useMedia";
import { useFeedback } from "~/composables/feedback/useFeedback";
import Tooltip from "~/components/Common/Tooltip.vue";
import type { BatchDocumentLinkResponse, SessionDocumentsClass } from "@simbelapp/order-sdk";
import { useNotificationsStore } from "~/store/notifications/notifications.store";

const { t } = useI18n();

const togglePaginate = ref(false);

useFeedback();
const displayedDocuments = ref(3);
const indexGrid = ref();
const uploadModal: Ref<boolean> = ref(false);
const notificationStore = useNotificationsStore();
const { createSession } = storeToRefs(notificationStore);

defineExpose({ uploadModal });

const hasRun = ref(false);
const h = [
  {
    name: "Nom",
    visible: true,
    minWidthPx: 110,
    maxWidthFr: 1,
  },
  {
    name: "Type",
    visible: true,
    minWidthPx: 110,
    maxWidthFr: 1,
  },
  {
    name: "Pour",
    visible: true,
    minWidthPx: 110,
    maxWidthFr: 1,
  },
];

const rows = computed(() => {
  if (createSession?.value?.documents?.length) {
    createSession?.value?.documents.sort((a, b) => {
      return new Date(b.createdAt ?? "").getTime() - new Date(a.createdAt ?? "").getTime();
    });
    return {
      data:
        !togglePaginate.value && createSession?.value?.documents.length > 3
          ? createSession?.value?.documents.slice(0, 3)
          : createSession?.value?.documents,
      meta: {
        count: createSession?.value?.documents.length,
      },
    };
  }

  return [];
});

const toggleShowMore = ref<boolean>(false);
const nbElements = ref<number>(1);

watch(
  () => indexGrid.value?.selectedItems,
  () => {
    if (createSession.value) createSession.value.selectedDocuments = indexGrid.value.selectedItems;
    if (!hasRun.value && createSession.value?.batchDocuments?.length) {
      indexGrid.value.selectedItems =
        createSession.value?.documents?.filter((d: SessionDocumentsClass) =>
          createSession.value?.batchDocuments?.some((bd: BatchDocumentLinkResponse) => bd.documentId === d.documentId),
        ) || [];
      hasRun.value = true;
    }
  },
);

watch(
  () => toggleShowMore.value,
  () => {
    if (!toggleShowMore.value && createSession?.value?.documents && createSession?.value?.documents.length > 3) {
      nbElements.value = 3;
    } else if (createSession?.value?.documents && createSession?.value?.documents.length) {
      nbElements.value = createSession?.value?.documents.length;
    }
  },
);

const isMobile = ref(useMedia(ScreenEnumType.mobile));
</script>

<template>
  <div v-if="!isMobile">
    <h1 class="heading_3_demi text-primary-color pb-6">Sélectionner des documents</h1>
    <div class="grid w-full px-4">
      <span class="body_regular text-primary-color pb-2"
        >Chaque participant recevra un mail personnel contenant les documents qui lui sont associés et ceux pour
        tous.</span
      >
      <button
        class="flex items-center button_secondary_default whitespace-nowrap body_regular py-8"
        @click.prevent="uploadModal = !uploadModal"
      >
        <SvgIcon class="svg_icon" name="add"></SvgIcon>
        <span>Ajouter des documents</span>
      </button>
      <div class="pt-2 overflow-y-scroll max-h-64">
        <IndexGrid
          ref="indexGrid"
          :headers="h"
          :no-pagination="true"
          :selected-nav="null"
          :checkbox-items-disabled="createSession?.batchDocuments"
          :rows="rows as IPaginationApi<any>"
          track-by="documentId"
          :checkbox="true"
          :item-per-page="3"
        >
          <template #row="{ row }">
            <td>
              <div class="index_row index_row_double-line">
                <Tooltip
                  v-if="
                    createSession?.batchDocuments?.some(
                      (bd: BatchDocumentLinkResponse) => bd.documentId === row.documentId,
                    )
                  "
                  :position="'top-left'"
                  append-to-body
                  minified
                  :force-position-top="5"
                >
                  <template #img>
                    <span class="text-tertiary-color">{{ row.fileName }}</span>
                  </template>
                  <template #content>
                    <div class="flex w-[312px] gap-[8px]">
                      <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
                      <span class="font-medium">Ce document est déjà en pièce-jointe</span>
                    </div>
                  </template>
                </Tooltip>
                <div v-else>
                  {{ row.fileName }}
                </div>
              </div>
            </td>
            <td>
              <div class="index_row">
                <div v-if="row.documentType">
                  <Tag :tag="{ value: t(`session.${row.documentType}`), type: 'grey', icon: 'document' }" />
                </div>
              </div>
            </td>
            <td>
              <div class="index_row">
                <Tag
                  :tag="{
                    value: row.displayValue || (row.adminOnly ? `${t('Aucun')}` : `${t('Everyone')}`),
                    type: 'grey',
                    icon: row.displayValue || row.adminOnly ? 'indiv' : 'group',
                  }"
                />
              </div>
            </td>
          </template>
        </IndexGrid>
      </div>
    </div>
    <div
      v-if="createSession?.documents && createSession?.documents.length > 3"
      class="flex justify-center mt-4 sm:mb-[100px] md:mb-[0px]"
    >
      <button class="py-[3px] text-[14px] button_secondary_default" @click="togglePaginate = !togglePaginate">
        {{ !togglePaginate ? `Afficher plus (${createSession?.documents.length - 3})` : "Afficher moins" }}
      </button>
    </div>
  </div>
  <div v-else>
    <div class="grid w-full">
      <h3 class="heading_3_med mb-4">Documents</h3>
      <div v-for="(document, i) in createSession?.documents" :key="i" v-if="createSession?.documents?.length">
        <div
          v-if="(i < displayedDocuments || togglePaginate) && i < createSession?.documents.length"
          class="flex flex-wrap gap-2 mb-4 items-center self-stretch px-4"
        >
          <span class="text-ellipsis underline min-w-full">{{ document.fileName }}</span>
          <Tag :tag="{ value: document.displayValue ?? t('Tous'), type: 'grey', icon: 'document' }" />
        </div>
      </div>
      <div v-else class="flex gap-4 text-tertiary-color items-center body_regular px-4">
        <SvgIcon name="attached-document-bold" class="min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px]"></SvgIcon>
        <span>Aucun document n’a été ajouté pour cette session.</span>
      </div>
      <div
        v-if="createSession?.documents && createSession?.documents.length > displayedDocuments"
        class="flex justify-center mt-2"
      >
        <button
          class="py-[3px] text-[14px] button_secondary_default mb-[100px]"
          @click="togglePaginate = !togglePaginate"
        >
          {{
            !togglePaginate
              ? `Afficher plus (${createSession?.documents.length - displayedDocuments})`
              : "Afficher moins"
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.index_row_double-line div {
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  display: -webkit-box;
  white-space: pre-wrap !important;
}
</style>
